import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../../config/api';
import PageHeader from '../../components/PageHeader';

interface Subscriber {
  id: number;
  email: string;
  name: string | null;
  active: boolean;
  preferences: {
    rasff_daily?: boolean;
    legislation_weekly?: boolean;
    legislation_stage_alerts?: boolean;
    major_incident_alerts?: boolean;
  };
  created_at: string;
}

async function fetchSubscribers(): Promise<Subscriber[]> {
  const res = await authFetch(`${API_BASE_URL}/api/notifications/subscribers`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function createSubscriber(data: { email: string; name: string; preferences: object }): Promise<Subscriber> {
  const res = await authFetch(`${API_BASE_URL}/api/notifications/subscribers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to create subscriber');
  }
  return res.json();
}

async function updateSubscriber(id: number, data: Partial<Subscriber>): Promise<Subscriber> {
  const res = await authFetch(`${API_BASE_URL}/api/notifications/subscribers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function deleteSubscriber(id: number): Promise<void> {
  const res = await authFetch(`${API_BASE_URL}/api/notifications/subscribers/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
}

function ToggleSwitch({ checked, onChange, label }: { checked: boolean; onChange: () => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`toggle-switch ${checked ? 'toggle-switch--on' : ''}`}
      title={checked ? 'Enabled — click to disable' : 'Disabled — click to enable'}
    >
      <span className="toggle-switch__knob" />
    </button>
  );
}

export default function NotificationsPage() {
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRasff, setNewRasff] = useState(true);
  const [newWeekly, setNewWeekly] = useState(true);
  const [formError, setFormError] = useState('');

  const { data: subscribers = [], isLoading, error } = useQuery({
    queryKey: ['notification-subscribers'],
    queryFn: fetchSubscribers,
  });

  const createMutation = useMutation({
    mutationFn: createSubscriber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-subscribers'] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-summary'] });
      setNewEmail('');
      setNewName('');
      setNewRasff(true);
      setNewWeekly(true);
      setFormError('');
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Subscriber> }) =>
      updateSubscriber(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-subscribers'] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-summary'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSubscriber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-subscribers'] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-summary'] });
    },
  });

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail.trim()) return;
    createMutation.mutate({
      email: newEmail.trim(),
      name: newName.trim(),
      preferences: { rasff_daily: newRasff, legislation_weekly: newWeekly, legislation_stage_alerts: false, major_incident_alerts: true },
    });
  }

  function toggleActive(sub: Subscriber) {
    updateMutation.mutate({ id: sub.id, data: { active: !sub.active } });
  }

  function togglePref(sub: Subscriber, key: 'rasff_daily' | 'legislation_weekly' | 'legislation_stage_alerts' | 'major_incident_alerts') {
    updateMutation.mutate({
      id: sub.id,
      data: { preferences: { ...sub.preferences, [key]: !sub.preferences[key] } },
    });
  }

  const activeCount = subscribers.filter(s => s.active).length;

  return (
    <div className="container admin-page">
      <PageHeader
        title="Notifications"
        tagline="Manage subscribers for daily RASFF alerts and weekly food safety digests."
        back={{ to: '/admin', label: 'Back to Manage' }}
      />

      <section className="card admin-section">
        <div className="card-header">
          <h3>Add subscriber</h3>
        </div>
        <div className="card-body admin-section__body">
          <form onSubmit={handleAdd} className="admin-form">
            <div className="admin-form__field admin-form__field--grow">
              <label className="admin-form__label">Email *</label>
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="person@example.com"
                required
                className="form-input"
              />
            </div>
            <div className="admin-form__field">
              <label className="admin-form__label">Name</label>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Optional"
                className="form-input"
              />
            </div>
            <div className="admin-form__field">
              <label className="admin-form__label">Notifications</label>
              <div className="admin-form__checks">
                <label className="admin-check">
                  <input type="checkbox" checked={newRasff} onChange={e => setNewRasff(e.target.checked)} />
                  <span>Daily RASFF alert</span>
                </label>
                <label className="admin-check">
                  <input type="checkbox" checked={newWeekly} onChange={e => setNewWeekly(e.target.checked)} />
                  <span>Weekly digest</span>
                </label>
              </div>
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="btn btn--primary admin-form__submit"
            >
              {createMutation.isPending ? 'Adding…' : 'Add subscriber'}
            </button>
          </form>
          {formError && <p className="admin-error">{formError}</p>}
        </div>
      </section>

      <section className="card admin-section">
        <div className="card-header">
          <h3>Subscribers</h3>
          <span className="card-badge">{activeCount} active · {subscribers.length} total</span>
        </div>
        <div className="card-body admin-section__body admin-section__body--flush">
          {isLoading && <p className="admin-muted admin-section__padded">Loading…</p>}
          {error && <p className="admin-error admin-section__padded">Failed to load subscribers.</p>}

          {!isLoading && subscribers.length === 0 && (
            <p className="admin-empty admin-section__padded">No subscribers yet. Add the first one above.</p>
          )}

          {subscribers.length > 0 && (
            <div className="data-table__wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Subscriber</th>
                    <th>Daily RASFF</th>
                    <th>Weekly digest</th>
                    <th>Law alerts</th>
                    <th>Incident alerts</th>
                    <th>Status</th>
                    <th>Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map(sub => (
                    <tr key={sub.id}>
                      <td>
                        <div className="admin-cell--primary">{sub.email}</div>
                        {sub.name && <div className="admin-muted admin-cell--secondary">{sub.name}</div>}
                      </td>
                      <td>
                        <ToggleSwitch
                          checked={!!sub.preferences?.rasff_daily}
                          onChange={() => togglePref(sub, 'rasff_daily')}
                          label="Daily RASFF"
                        />
                      </td>
                      <td>
                        <ToggleSwitch
                          checked={!!sub.preferences?.legislation_weekly}
                          onChange={() => togglePref(sub, 'legislation_weekly')}
                          label="Weekly digest"
                        />
                      </td>
                      <td>
                        <ToggleSwitch
                          checked={!!sub.preferences?.legislation_stage_alerts}
                          onChange={() => togglePref(sub, 'legislation_stage_alerts')}
                          label="Legislation stage alerts"
                        />
                      </td>
                      <td>
                        <ToggleSwitch
                          checked={!!sub.preferences?.major_incident_alerts}
                          onChange={() => togglePref(sub, 'major_incident_alerts')}
                          label="Major incident alerts"
                        />
                      </td>
                      <td>
                        <button
                          onClick={() => toggleActive(sub)}
                          className={`status-pill-btn ${sub.active ? 'status-pill-btn--active' : 'status-pill-btn--paused'}`}
                        >
                          {sub.active ? 'Active' : 'Paused'}
                        </button>
                      </td>
                      <td className="admin-muted admin-cell--nowrap">
                        {new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td>
                        <button
                          onClick={() => {
                            if (confirm(`Remove ${sub.email}?`)) deleteMutation.mutate(sub.id);
                          }}
                          className="btn btn--danger-ghost btn--sm"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <aside className="admin-info">
        <strong>How it works:</strong> Daily RASFF alerts fire automatically after the nightly ingestion
        pipeline (~03:00 CET) when serious or urgent risk decisions are detected. The weekly digest runs
        every Monday at 08:00 CET and includes an AI-written briefing covering legislation, RASFF highlights,
        and recalls from the past 7 days.
      </aside>
    </div>
  );
}
